/* Campaign presentation windows, independent of Shopify's cached render time. */
(() => {
  const previewParameter = 'show-all-campaigns';
  function previewIsRequested(search) {
    return new URLSearchParams(search).get(previewParameter) === 'true';
  }
  function withCampaignPreview(href, currentUrl) {
    try {
      const current = new URL(currentUrl), link = new URL(href, current);
      if (href.trim().startsWith('#') || !/^https?:$/.test(link.protocol) || link.origin !== current.origin) return href;
      link.searchParams.set(previewParameter, 'true');
      return link.href;
    } catch (_) { return href; }
  }
  function withoutCampaignPreview(currentUrl) {
    const url = new URL(currentUrl);
    url.searchParams.delete(previewParameter);
    return url.href;
  }
  function boundary(value) {
    if (value === '' || value == null) return null;
    return /^-?\d+$/.test(value) ? Number(value) * 1000 : NaN;
  }
  function campaignIsActive(data, now, preview = false) {
    const start = boundary(data.start), end = boundary(data.end);
    if (data.enabled !== 'true' || !Number.isFinite(now)) return false;
    if ((start !== null && !Number.isFinite(start)) || (end !== null && !Number.isFinite(end))) return false;
    if (start !== null && end !== null && end <= start) return false;
    return preview || ((start === null || now >= start) && (end === null || now < end));
  }

  function selectBanners(candidates, now, preview = false) {
    const rank = candidate => {
      const parsed = Number(candidate.dataset.rank);
      return candidate.dataset.rank === '' || !Number.isFinite(parsed) ? 1000000 : parsed;
    };
    return candidates.filter(candidate => campaignIsActive(candidate.dataset, now, preview))
      .sort((a, b) => rank(a) - rank(b));
  }
  function selectBanner(candidates, now, preview = false) {
    return selectBanners(candidates, now, preview)[0] || null;
  }
  function countdown(endValue, now, language = 'sv') {
    const end = boundary(endValue);
    if (!Number.isFinite(end) || !Number.isFinite(now)) return null;
    const remaining = Math.max(0, end - now), totalMinutes = Math.floor(remaining / 60000);
    const days = Math.floor(totalMinutes / 1440), hours = Math.floor(totalMinutes / 60) % 24;
    const minutes = totalMinutes % 60;
    const translations = {
      fr: {prefix:'Temps restant', before:'Encore ', after:'', shortHour:'h', shortMinute:'min', less:'Moins d’une minute', day:['jour','jours'], hour:['heure','heures'], minute:['minute','minutes']},
      sv: {prefix:'Tid kvar', before:'', after:' kvar', shortHour:'tim', shortMinute:'min', less:'Mindre än 1 min kvar', day:['dag','dagar'], hour:['timme','timmar'], minute:['minut','minuter']},
      de: {prefix:'Verbleibende Zeit', before:'Noch ', after:'', shortHour:'Std.', shortMinute:'Min.', less:'Noch weniger als 1 Min.', day:['Tag','Tage'], hour:['Stunde','Stunden'], minute:['Minute','Minuten']},
      en: {prefix:'Time remaining', before:'', after:' left', shortHour:'hr', shortMinute:'min', less:'Less than 1 min left', day:['day','days'], hour:['hour','hours'], minute:['minute','minutes']}
    };
    const words = translations[language] || translations.en;
    if (remaining > 0 && totalMinutes === 0) return {text:words.less, label:words.less};
    const unit = (value, key) => value + ' ' + words[key][value === 1 ? 0 : 1];
    const compact = [], spoken = [];
    if (days > 0) { compact.push(unit(days, 'day')); spoken.push(unit(days, 'day')); }
    if (days > 0 || hours > 0) { compact.push(hours + ' ' + words.shortHour); spoken.push(unit(hours, 'hour')); }
    compact.push(minutes + ' ' + words.shortMinute);
    spoken.push(unit(minutes, 'minute'));
    return {text:words.before + compact.join(' ') + words.after, label:words.prefix + ': ' + spoken.join(', ')};
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { campaignIsActive, boundary, previewIsRequested, withCampaignPreview, withoutCampaignPreview, selectBanner, selectBanners, countdown };
  if (typeof customElements === 'undefined' || customElements.get('wg-campaign-slot')) return;
  const preview = previewIsRequested(window.location.search);
  function showPreviewNotice() {
    if (!preview || document.getElementById('wg-campaign-preview-notice')) return;
    const language = document.documentElement.lang.split('-')[0];
    const labels = {
      fr: ['Aperçu des campagnes : les dates ne sont pas prises en compte', 'Quitter l’aperçu'],
      sv: ['Förhandsvisning av kampanjer – datum ignoreras', 'Avsluta förhandsvisning'],
      de: ['Kampagnenvorschau – Zeitplan wird ignoriert', 'Vorschau beenden'],
      en: ['Campaign preview – dates are ignored', 'Exit preview']
    };
    const [message, exitLabel] = labels[language] || labels.en;
    const notice = document.createElement('div');
    notice.id = 'wg-campaign-preview-notice';
    notice.style.cssText = 'display:flex;flex-wrap:wrap;justify-content:center;align-items:center;gap:8px 20px;padding:10px 16px;background:#222;color:#fff;font:14px/1.4 sans-serif;text-align:center';
    const text = document.createElement('span');
    text.textContent = message;
    const exit = document.createElement('a');
    exit.href = withoutCampaignPreview(window.location.href);
    exit.textContent = exitLabel;
    exit.style.cssText = 'color:#fff;text-decoration:underline';
    notice.append(text, exit);
    document.body.prepend(notice);
  }

  customElements.define('wg-campaign-banners', class extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.content = this.querySelector('[data-banner-content]');
        this.candidates = Array.from(this.querySelectorAll('template[data-banner]'));
        if (!this.content) return;
        this.selected = [];
        this.items = [];
        this.refresh = () => this.update();
        document.addEventListener('visibilitychange', this.refresh);
        window.addEventListener('pageshow', this.refresh);
        window.addEventListener('focus', this.refresh);
        showPreviewNotice();
        this.update();
      });
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.timer);
      if (this.refresh) {
        document.removeEventListener('visibilitychange', this.refresh);
        window.removeEventListener('pageshow', this.refresh);
        window.removeEventListener('focus', this.refresh);
      }
    }
    update() {
      clearTimeout(this.timer);
      const now = Date.now(), selected = selectBanners(this.candidates, now, preview);
      if (selected.length !== this.selected.length || selected.some((candidate, index) => candidate !== this.selected[index])) {
        this.items = selected.map(candidate => {
          const fragment = candidate.content.cloneNode(true);
          return {candidate, fragment, timer: fragment.querySelector('[data-countdown]')};
        });
        this.content.replaceChildren(...this.items.map(item => item.fragment));
        this.selected = selected;
        if (preview) this.content.querySelectorAll('a[href]').forEach(link => {
          link.setAttribute('href', withCampaignPreview(link.getAttribute('href'), window.location.href));
        });
      }
      this.hidden = selected.length === 0;
      this.dataset.hideDefaultBar = String(selected.some(candidate => candidate.dataset.hideDefaultBar === 'true'));
      this.dataset.campaign = selected.length ? selected[0].dataset.campaign : '';
      this.dataset.campaigns = selected.map(candidate => candidate.dataset.campaign).join(' ');
      this.dataset.preview = String(preview);
      let hasRunningTimer = false;
      this.items.forEach(({candidate, timer}) => {
        if (!timer) return;
        const remaining = countdown(candidate.dataset.end, now, candidate.dataset.language);
        timer.hidden = !remaining;
        if (remaining) {
          const value = timer.querySelector('[data-countdown-value]');
          value.textContent = remaining.text;
          value.setAttribute('aria-label', remaining.label);
          if (boundary(candidate.dataset.end) > now) hasRunningTimer = true;
        }
      });
      if (document.hidden) return;
      const future = preview ? [] : this.candidates.filter(candidate => candidate.dataset.enabled === 'true')
        .flatMap(candidate => [boundary(candidate.dataset.start), boundary(candidate.dataset.end)])
        .filter(value => Number.isFinite(value) && value > now);
      let delay = future.length ? Math.min(60000, Math.max(1, Math.min(...future) - now)) : Infinity;
      if (hasRunningTimer) delay = Math.min(delay, 1000 - now % 1000);
      if (Number.isFinite(delay)) this.timer = setTimeout(() => this.update(), delay);
    }
  });
  customElements.define('wg-campaign-slot', class extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.content = this.querySelector('[data-content]');
        this.activeTemplate = this.querySelector('template[data-active]');
        this.fallbackTemplate = this.querySelector('template[data-fallback]');
        if (!this.content || !this.activeTemplate || !this.fallbackTemplate) return;
        this.state = this.dataset.initial;
        this.refresh = () => this.update();
        document.addEventListener('visibilitychange', this.refresh);
        window.addEventListener('pageshow', this.refresh);
        window.addEventListener('focus', this.refresh);
        showPreviewNotice();
        this.update();
      });
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.timer);
      if (this.refresh) {
        document.removeEventListener('visibilitychange', this.refresh);
        window.removeEventListener('pageshow', this.refresh);
        window.removeEventListener('focus', this.refresh);
      }
    }
    update() {
      clearTimeout(this.timer);
      const now = Date.now();
      const active = campaignIsActive(this.dataset, now, preview);
      const state = active ? 'active' : 'fallback';
      if (state !== this.state) {
        this.content.replaceChildren((active ? this.activeTemplate : this.fallbackTemplate).content.cloneNode(true));
        this.state = state;
      }
      if (preview && active) {
        this.content.querySelectorAll('a[href]').forEach(link => {
          link.setAttribute('href', withCampaignPreview(link.getAttribute('href'), window.location.href));
        });
      }
      this.hidden = !active && this.dataset.placement !== 'collection';
      this.dataset.state = state;
      this.dataset.preview = String(preview);
      const future = [boundary(this.dataset.start), boundary(this.dataset.end)].filter(value => Number.isFinite(value) && value > now);
      if (!preview && future.length && this.dataset.enabled === 'true') {
        this.timer = setTimeout(() => this.update(), Math.min(60000, Math.max(16, Math.min(...future) - now + 16)));
      }
    }
  });
})();
